import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useConsents, useConsentStatistics, useAccessStatistics } from '../../../hooks/useOpenFinance';
import type { ConsentStatus } from '../../../types/open-finance';

const statusColors: Record<ConsentStatus, 'success' | 'primary' | 'default' | 'error'> = {
  AWAITING_AUTHORIZATION: 'primary',
  AUTHORIZED: 'success',
  REJECTED: 'error',
  REVOKED: 'default',
  EXPIRED: 'default',
};

const statusLabels: Record<ConsentStatus, string> = {
  AWAITING_AUTHORIZATION: 'Aguardando',
  AUTHORIZED: 'Autorizado',
  REJECTED: 'Rejeitado',
  REVOKED: 'Revogado',
  EXPIRED: 'Expirado',
};

export function OpenFinanceMonitor() {
  const [statusFilter, setStatusFilter] = useState<ConsentStatus | undefined>();
  const { data: consentsData, isLoading: loadingConsents } = useConsents({ status: statusFilter, limit: 10 });
  const { data: stats, isLoading: loadingStats } = useConsentStatistics();
  const { data: accessStats, isLoading: loadingAccess } = useAccessStatistics('30d');

  if (loadingStats || loadingConsents) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Monitor Open Finance</h1>
        <p className="text-gray-500">Acompanhe consentimentos e acessos a dados</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
            <p className="text-sm text-gray-500">Total de Consentimentos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{stats?.active || 0}</div>
            <p className="text-sm text-gray-500">Ativos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-gray-500">{stats?.revoked || 0}</div>
            <p className="text-sm text-gray-500">Revogados</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-orange-500">{stats?.expired || 0}</div>
            <p className="text-sm text-gray-500">Expirados</p>
          </CardContent>
        </Card>
      </div>

      {/* Access Stats */}
      {!loadingAccess && accessStats && (
        <Card>
          <CardHeader>
            <CardTitle>Acessos nos Últimos 30 Dias</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-3xl font-bold">{accessStats.totalRequests}</p>
                <p className="text-sm text-gray-500">Total de Requisições</p>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Por Tipo de Dado</p>
                <div className="space-y-1">
                  {accessStats.byDataType?.map((item) => (
                    <div key={item.dataType} className="flex justify-between text-sm">
                      <span>{item.dataType}</span>
                      <span className="font-medium">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top TPPs */}
      {stats?.byTpp && stats.byTpp.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top TPPs por Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.byTpp.slice(0, 5).map((tpp) => (
                <div key={tpp.tppId} className="flex items-center justify-between">
                  <span className="font-medium">{tpp.tppName}</span>
                  <Badge variant="primary">{tpp.count} consentimentos</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Consents List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Consentimentos Recentes</CardTitle>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={!statusFilter ? 'primary' : 'secondary'}
                onClick={() => setStatusFilter(undefined)}
              >
                Todos
              </Button>
              <Button
                size="sm"
                variant={statusFilter === 'AUTHORIZED' ? 'primary' : 'secondary'}
                onClick={() => setStatusFilter('AUTHORIZED')}
              >
                Ativos
              </Button>
              <Button
                size="sm"
                variant={statusFilter === 'REVOKED' ? 'primary' : 'secondary'}
                onClick={() => setStatusFilter('REVOKED')}
              >
                Revogados
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {consentsData?.consents && consentsData.consents.length > 0 ? (
            <div className="space-y-3">
              {consentsData.consents.map((consent) => (
                <div
                  key={consent.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div>
                    <p className="font-medium">{consent.tppName}</p>
                    <p className="text-sm text-gray-500">
                      {consent.scopes.join(', ')} • Expira em{' '}
                      {new Date(consent.expiresAt).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <Badge variant={statusColors[consent.status]}>
                    {statusLabels[consent.status]}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Nenhum consentimento encontrado
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default OpenFinanceMonitor;
